<template>
  <div class="user-statistics">
        <form @submit.prevent="() => search()" class="main-form search-form">
            <custom-select
                v-if="isSupervisor"
                :options="webshopOptions"
                :title="$t('USER_STATS__WEBSHOP')"
                v-model="filter.webshopID"
                @clear="filter.webshopID = ''"
            />
            <custom-input
                :title="$t('USER_STATS__USERNAME')"
                v-model="filter.username"
                @clear="filter.username = ''"
            />
            <date-picker 
                :title="$t('USER_STATS__START_DATE')"
                v-model="filter.startDate"
                @clear="filter.startDate = ''"
            />
            <date-picker 
                :title="$t('USER_STATS__TO_DATE')"
                v-model="filter.toDate"
                @clear="filter.toDate = ''"
            />
            <button class="btn">{{$t('USER_STATS__SEARCH')}}</button>
        </form>
        <grid 
            v-show="!loading"
            fullGrid
            :title="$t('USER_STATS__USER_STATISTICS')"
            :passedData="reportData"
            :passedCount="count"
            :passedPageSize="pageSize"
            :columns="reportColumns"
            @pageChanged="page => search(page)"
            @sort="orderingData => changeOrdering(orderingData)"
        />
        <div class="loader" v-show="loading"></div>
        <custom-snackbar
            :purpose="mainSnackbar.purpose"
            :text="mainSnackbar.text"
            v-if="mainSnackbar.visible"
            @close="mainSnackbar.visible = false"
        />
  </div>
</template>

<script>
import DatePicker from '@/components/ReusableComponents/DatePicker/DatePicker.vue'
import CustomInput from '@/components/ReusableComponents/CustomInput/CustomInput.vue'
import CustomSelect from '@/components/ReusableComponents/CustomSelect/CustomSelect.vue'
import CustomSnackbar from '@/components/ReusableComponents/CustomSnackbar/CustomSnackbar.vue'
import Grid from '@/components/ReusableComponents/Grid/Grid.vue'
import axios from '@/Helpers/AxiosInstance.js'
import { getDateString } from '@/Helpers/HelperFunctions.js'
export default {
    components: {
        DatePicker,
        CustomInput,
        Grid,
        CustomSelect,
        CustomSnackbar,
    },
    created() {
        this.setDefaultDateFilters()
        this.watchDates()
        this.search()
        if(!this.isSupervisor) {
            this.setWebshopID()
        }
    },
    data() {
        return {
            filter: {
                username: '',
                startDate: '',
                toDate: '',
                webshopID: '',
            },
            loading: false,
            reportData: [],
            count: 0,
            pageSize: 20,
            reportColumns: [
                {
                    title: this.$t('USER_STATS__USERNAME'),
                    key: 'userName',
                },
                {
                    title: this.$t('USER_STATS__BALANCE'),
                    key: 'balanceDecimal',
                    customTemplate: (item, val) => val + ' ' + item.currencyCode
                },
                {
                    title: this.$t('USER_STATS__DEPOSIT'),
                    key: 'depositAmountTotal',
                    customTemplate: (item, val) => val + ' ' + item.currencyCode   
                },
                {
                    title: this.$t('USER_STATS__WITHDRAW'),
                    key: 'withdrawAmountTotal',
                    customTemplate: (item, val) => val + ' ' + item.currencyCode
                },
                {
                    title: this.$t('USER_STATS__PAYMENTDIFF'),
                    key: 'paymentDiff',
                },
                {
                    title: this.$t('USER_STATS__BET_AMOUNT'),
                    key: 'betAmount',
                    customTemplate: (item, val) => val + ' ' + item.currencyCode
                },
                {
                    title: this.$t('USER_STATS__WIN_AMOUNT'),
                    key: 'winAmount',
                    customTemplate: (item, val) => val + ' ' + item.currencyCode
                },
                {
                    title: this.$t('USER_STATS__PROFIT'),
                    key: 'profitTotal',
                    customTemplate: (item, val) => val + ' ' + item.currencyCode   
                },
            ],
            mainSnackbar: {
                purpose: 'error',
                text: '',
                visible: false,
            },
            orderColumn: 'userId',
            orderDirection: 'desc',
            isDateError: false,
        }
    },
    methods: {
        setDefaultDateFilters() {
            var d = new Date()
            d.setDate(1)
            this.filter.startDate = getDateString(d, 'yyyy-MM-dd')
            this.filter.toDate = getDateString(new Date(), 'yyyy-MM-dd')
        },
        watchDates() {
            this.$watch('filter', () => this.checkForDateErrors(), {deep: true})
        },
        search(page = 1) {
            this.checkForDateErrors()
            if(this.isDateError) return
            this.loading = true
            axios
                .get('WebShop/GetShopUserStatsReport', {
                    params: {
                        CurrentPage: page,
                        PageSize: this.pageSize,
                        WebShopId: this.filter.webshopID,
                        UserName: this.filter.username,
                        StartDate: this.filter.startDate,
                        ToDate: this.filter.toDate,
                        OrderColumn: this.orderColumn,
                        OrderDirection: this.orderDirection,
                    }
                })
                .then(resp => {
                    this.loading = false
                    if(resp.data.status != 1) {
                        this.reportData = []
                        this.count = 0
                        this.showError(resp.data.status)
                    }
                    else {
                        this.reportData = resp.data.response
                        this.count = this.reportData.length > 0 ? this.reportData[0].count : 0
                    }
                })
                .catch(() => {
                    this.showError()
                })
        },
        setWebshopID() {
            this.filter.webshopID = this.$store.state.webshops[0].id
        },
        showError(status) {
            this.mainSnackbar.visible = true
            this.mainSnackbar.purpose = 'error'
            this.mainSnackbar.text = 'An Error Occurred While Canceling!'
            var code = this.statusCodes.filter(e => e.key == status)[0]
            if (code) this.mainSnackbar.text = this.$t('STATUS-CODE__' + code.value)
        },
        changeOrdering(orderingData) {
            this.orderDirection = orderingData.orderByDesc ? 'desc' : 'asc'
            this.orderColumn = orderingData.columnName
            this.search()
        },
        checkForDateErrors() {
            let start = this.filter.startDate
            let end = this.filter.toDate
            if(!start || !end) {
                this.isDateError = true
                this.showInvalidDateError()
                return
            }
            const getTime = str => new Date(str).getTime()
            const timeToDays = time => time / 86400000
            let differenceInDays = timeToDays(getTime(end) - getTime(start))
            if(differenceInDays < 0) {
                this.isDateError = true
                this.showInvalidDateError()
                return
            }
            if(differenceInDays > 31) {
                this.isDateError = true
                this.showDateIntervalTooBigError()
                return
            }
            this.isDateError = false
            this.mainSnackbar.visible = false
        },
        showInvalidDateError() {
            this.mainSnackbar.visible = true
            this.mainSnackbar.purpose = 'error'
            this.mainSnackbar.text = 'Invalid Dates'
        },
        showDateIntervalTooBigError() {
            this.mainSnackbar.visible = true
            this.mainSnackbar.purpose = 'error'
            this.mainSnackbar.text = 'Maximum Date Range Allowed Is 1 Month'
        }
    },
    computed: {
        isSupervisor() {
            return this.$store.state.shop
        },
        webshopOptions() {
            var options = []
            this.$store.state.webshops.forEach(shop => {
                let title = shop.name
                    ? `${shop.calculatedName} (${shop.name})`
                    : shop.calculatedName
                options.push({ title: title, value: shop.id + '' })
            })
            return options
        },
        statusCodes() {
            return this.$store.state.statusCodes
        },
    },
}
</script>

<style scoped lang="scss">
    .search-form {
        margin-bottom: 0.9375rem;
        max-width: 62.5rem;
        display: flex;
    }
</style>